import { useState, useEffect } from "react";
import "./IntroEnvelope.css";
import { trackMediaActivity } from "../lib/mediaTracking";

// Fallback defaults if no `config` prop is passed
const FALLBACK = {
  logo_url: "/intro/logo.png",
  lacre_url: "/intro/lacre.png",
  lavanda_url: "/intro/lavanda.png",
  paragraph_1: "Hay momentos en la vida que son especiales por sí solos.",
  paragraph_2:
    "Compartirlos con las personas que quieres los convierte en inolvidables.",
  button_text: "Pincha para entrar",
  boton_modo_pc: "inicial",
  boton_offset_pc_px: 0,
  boton_modo_movil: "inicial",
  boton_offset_movil_px: 0,
};

// v73.54 — Detecta si el viewport es móvil (<=768px de ancho)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

const IntroEnvelope = ({ onEnter, config }) => {
  const cfg = { ...FALLBACK, ...(config || {}) };
  const LOGO_URL = cfg.logo_url || FALLBACK.logo_url;
  const LACRE_URL = cfg.lacre_url || FALLBACK.lacre_url;
  const LAVANDA_URL = cfg.lavanda_url || FALLBACK.lavanda_url;
  const CARD_TEXT_PARA1 = cfg.paragraph_1 || FALLBACK.paragraph_1;
  const CARD_TEXT_PARA2 = cfg.paragraph_2 || FALLBACK.paragraph_2;
  const BUTTON_TEXT = cfg.button_text || FALLBACK.button_text;
  // Logo vertical position inside the flap interior (% from top).
  // Default 38% matches the original CSS placement.
  const LOGO_OFFSET_Y =
    cfg.logo_offset_y === undefined || cfg.logo_offset_y === null
      ? 38
      : Math.max(0, Math.min(100, Number(cfg.logo_offset_y)));

  // v73.54 — Modo y offset del botón "Pincha para entrar" (PC vs móvil)
  const isMobile = useIsMobile();
  const btnMode = isMobile
    ? (cfg.boton_modo_movil || "inicial")
    : (cfg.boton_modo_pc || "inicial");
  const btnOffsetPx = Number(
    isMobile ? (cfg.boton_offset_movil_px || 0) : (cfg.boton_offset_pc_px || 0)
  ) || 0;

  // phases: idle (closed) -> opening (flap opens) -> waiting (2s pause) -> sliding (card 4s) -> done
  const [phase, setPhase] = useState("idle");

  useEffect(() => {
    if (phase === "opening") {
      const t1 = setTimeout(() => setPhase("waiting"), 1400); // flap open ~1.4s
      return () => clearTimeout(t1);
    }
    if (phase === "waiting") {
      const t2 = setTimeout(() => setPhase("sliding"), 2000); // 2s pause
      return () => clearTimeout(t2);
    }
    if (phase === "sliding") {
      const t3 = setTimeout(() => setPhase("done"), 4000); // 4s slide
      return () => clearTimeout(t3);
    }
  }, [phase]);

  const handleEnvelopeClick = () => {
    if (phase === "idle") {
      setPhase("opening");
      // Registra que el invitado ha "abierto" / visualizado la intro
      trackMediaActivity({
        accion: 'view_full',
        origen: 'intro',
        archivo_url: LOGO_URL || '/intro/logo.png',
        archivo_nombre: 'intro-sobre',
        archivo_tipo: 'imagen',
      });
    }
  };

  const flapOpen = phase !== "idle";
  const cardOut = phase === "sliding" || phase === "done";

  return (
    <div className="intro-root" data-testid="intro-root">
      <div
        className={`envelope-stage ${phase}`}
        data-testid="envelope-stage"
      >
        {/* Lavender side decorations — positioned RELATIVE to the stage
             so they scale proportionally with the envelope on every viewport. */}
        <img
          className="lavender lavender-left"
          src={LAVANDA_URL}
          alt=""
          aria-hidden="true"
          draggable="false"
        />
        <img
          className="lavender lavender-right"
          src={LAVANDA_URL}
          alt=""
          aria-hidden="true"
          draggable="false"
        />
        <div
          className={`envelope ${flapOpen ? "open" : ""} ${
            cardOut ? "card-out" : ""
          }`}
          onClick={handleEnvelopeClick}
          role="button"
          tabIndex={0}
          aria-label="Abrir sobre"
          data-testid="envelope-clickable"
        >
          {/* Drop shadow under envelope */}
          <div className="envelope-ground-shadow" />

          {/* Back of envelope (behind everything) */}
          <div className="envelope-back">
            <div className="paper-grain" />
          </div>

          {/* The card - slides upward */}
          <div className="card-wrapper">
            <div className="card-shadow" />
            <div className="card">
              <div className="card-border">
                <p className="card-text">{CARD_TEXT_PARA1}</p>
                <p className="card-text">{CARD_TEXT_PARA2}</p>
                <div className="card-heart">♥</div>
              </div>
            </div>
          </div>

          {/* Front pocket (V-shape covers bottom half) */}
          <div className="envelope-front">
            <div className="paper-grain" />
            <div className="front-fold front-fold-left" />
            <div className="front-fold front-fold-right" />
            <div className="front-fold front-fold-bottom" />
          </div>

          {/* Top flap (rotates open) - simple single triangle.
               Logo for the open-flap interior is rendered as a separate
               overlay below to avoid 3D card-flip rendering bugs. */}
          <div className="flap">
            <div className="flap-bg" />
            <div className="paper-grain" />
          </div>

          {/* Wax seal — sibling of the flap (not child) so the flap's
               clip-path doesn't cut it. Sits at the apex of the closed flap
               and stays in place even when the envelope opens. */}
          <div className="wax-seal" data-testid="wax-seal">
            <img src={LACRE_URL} alt="Lacre dorado" draggable="false" />
          </div>

          {/* Open-flap interior overlay: positioned exactly where the
               opened flap visually lands (above the envelope body,
               triangle pointing up). Fades in after the flap rotation. */}
          <div className="flap-interior" aria-hidden="true">
            <div className="flap-interior-bg" />
            <img
              className="flap-interior-logo"
              src={LOGO_URL}
              alt=""
              draggable="false"
              style={{ top: `${LOGO_OFFSET_Y}%` }}
            />
          </div>
        </div>

        {/* Hint to click - only when idle */}
        {phase === "idle" && (
          <div className="click-hint" data-testid="click-hint">
            Pincha el sobre para abrir
          </div>
        )}

        {/* v73.54 — Modo "sobre": botón anclado a la base del sobre
            (dentro de .envelope-stage). translateZ(120px) lo saca del
            contexto 3D para que no quede tapado por la solapa. */}
        {phase === "done" && btnMode === "sobre" && (
          <button
            className="enter-button enter-button--sobre"
            onClick={onEnter}
            data-testid="enter-button"
            style={{
              position: "absolute",
              bottom: `${btnOffsetPx}px`,
              left: "50%",
              transform: "translateX(-50%) translateZ(120px)",
            }}
          >
            <span>{BUTTON_TEXT}</span>
            <span className="enter-arrow">→</span>
          </button>
        )}
      </div>

      {/* Enter button — modo "inicial" (comportamiento original) */}
      {phase === "done" && btnMode === "inicial" && (
        <button
          className="enter-button"
          onClick={onEnter}
          data-testid="enter-button"
        >
          <span>{BUTTON_TEXT}</span>
          <span className="enter-arrow">→</span>
        </button>
      )}

      {/* v73.54 — Modo "pagina": anclado al borde inferior del viewport.
          La distancia desde el fondo es siempre `btnOffsetPx` px, así que
          se respeta en cualquier resolución. */}
      {phase === "done" && btnMode === "pagina" && (
        <button
          className="enter-button enter-button--pagina"
          onClick={onEnter}
          data-testid="enter-button"
          style={{
            position: "fixed",
            bottom: `${btnOffsetPx}px`,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <span>{BUTTON_TEXT}</span>
          <span className="enter-arrow">→</span>
        </button>
      )}
    </div>
  );
};

export default IntroEnvelope;
